// src/components/Nav.jsx
import { useEffect, useState, memo } from "react";
import { Link, useLocation } from "react-router-dom";
import { ConnectButton } from "@rainbow-me/rainbowkit";
import "../Layout.scss";
import BobberEligibility from "../sections/claimchecker/bobber/BobberEligibility";
import useEligibilityCheck from "../hooks/useEligibilityCheck";

const SHOW_AIRDROP = true; // temporary: enabled for bobber allowlist testing
const LAUNCHPAD_ROOT_HOSTS = new Set(["gbob.fun", "www.gbob.fun"]);

function Nav() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isHookHovered, setIsHookHovered] = useState(false);
  const [showBobberPopup, setShowBobberPopup] = useState(false);
  const [bobberTrigger, setBobberTrigger] = useState(0);
  const [bobberDone, setBobberDone] = useState(false);
  const [bobberEligible, setBobberEligible] = useState(false);
  const [bobberLoading, setBobberLoading] = useState(false);
  const [bobberMessage, setBobberMessage] = useState("");
  const location = useLocation();
  const { checkEligibility } = useEligibilityCheck();
  const path = (location?.pathname || "").toLowerCase();
  const host = typeof window !== "undefined" ? String(window.location.hostname || "").toLowerCase() : "";
  const isLaunchpadRootHost = LAUNCHPAD_ROOT_HOSTS.has(host);
  const isDashboardRoute = path.startsWith("/dashboard");
  const isLaunchpadRoute = isLaunchpadRootHost || path.startsWith("/launchpad");
  const launchpadBaseHref = isLaunchpadRootHost ? "/" : "/launchpad";
  const launchpadCreateHref = isLaunchpadRootHost ? "/create" : "/launchpad/create";

  useEffect(() => {
    const sources = [
      "/header/bobyes.jpg",
      "/header/bobno.jpg",
      "/icons/fishingrod.svg",
      "/icons/fishingrodhover.svg",
    ];

    sources.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  const closeBobberPopup = () => {
    setShowBobberPopup(false);
    setBobberTrigger(0);
    setBobberDone(false);
    setBobberEligible(false);
    setBobberLoading(false);
    setBobberMessage("");
  };

  const openBobberPopup = () => {
    setMenuOpen(false);
    setShowBobberPopup(true);
    setBobberTrigger(0);
    setBobberDone(false);
    setBobberEligible(false);
    setBobberLoading(false);
    setBobberMessage("");
  };

  const handleBobberCheck = async () => {
    if (bobberLoading) return;
    setBobberDone(false);
    setBobberLoading(true);
    setBobberMessage("");

    try {
      const result = await checkEligibility();
      const eligible = result?.state === "eligible" || result?.eligible === true;
      setBobberEligible(eligible);
      setBobberTrigger((prev) => prev + 1);
    } catch (error) {
      const msg = String(error?.message || "Verification failed, please retry");
      setBobberMessage(msg);
      setBobberLoading(false);
      return;
    }

    setBobberLoading(false);
  };

  return (
    <>
      {menuOpen && (
        <div
          className="nav-mobile-backdrop"
          onClick={() => setMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      <nav>
        <Link id="brand" to={isLaunchpadRoute ? launchpadBaseHref : "/"} aria-label="Home">
          <img
            id="logo"
            src={isLaunchpadRoute ? "/gbobfun.svg" : "/icons/bobdefault.svg"}
            alt={isLaunchpadRoute ? "gBob" : "BOB"}
          />
        </Link>

        <div id="menu">
          {/* MOBILE: connect wallet button */}
          <div className="nav-connect-mobile">
            <div className="nav-connect-row">
              <ConnectButton
                showBalance={false}
                label="Connect Wallet"
                chainStatus="none"
                accountStatus="address"
              />
            </div>
          </div>

          {SHOW_AIRDROP && (
            <button
              type="button"
              className="hook-button-mobile"
              onClick={openBobberPopup}
              onMouseEnter={() => setIsHookHovered(true)}
              onMouseLeave={() => setIsHookHovered(false)}
              aria-label="Open Bobber Eligibility Checker"
            >
              <img
                src={
                  isHookHovered
                    ? "/icons/fishingrodhover.svg"
                    : "/icons/fishingrod.svg"
                }
                alt=""
                aria-hidden="true"
              />
            </button>
          )}

          {/* hamburger */}
          <div
            id="menu-toggle"
            className={menuOpen ? "closeMenu" : ""}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
          >
            <div id="menu-icon">
              <span className="bar" />
              <span className="bar" />
              <span className="bar" />
            </div>
          </div>

          <ul className={menuOpen ? "showMenu" : ""}>
            {/* DESKTOP: connect wallet button */}
            <li className="nav-connect-desktop">
              <div className="nav-connect-row">
                <ConnectButton
                  showBalance={false}
                  label="Connect Wallet"
                  chainStatus="none"
                  accountStatus="address"
                />
              </div>
            </li>

            {SHOW_AIRDROP && (
              <li className="hook-link">
                <button
                  type="button"
                  className="hook-button"
                  onClick={openBobberPopup}
                  onMouseEnter={() => setIsHookHovered(true)}
                  onMouseLeave={() => setIsHookHovered(false)}
                  aria-label="Open Bobber Eligibility Checker"
                >
                  <img
                    src={
                      isHookHovered
                        ? "/icons/fishingrodhover.svg"
                        : "/icons/fishingrod.svg"
                    }
                    alt=""
                    aria-hidden="true"
                  />
                </button>
              </li>
            )}

            {isLaunchpadRoute ? (
              <>
                <li className="buy-link">
                    <Link
                    to={launchpadCreateHref}
                    className="presale-btn buy-btn"
                    aria-label="Create"
                  >
                    Create
                  </Link>
                </li>
              </>
            ) : (
              <>
                <li className="buy-link">
                  <a
                    href="https://nad.fun/tokens/0x21E325B059Cd83d4037C82F0F5998Ba2dF3d7777"
                    className="presale-btn buy-btn"
                    aria-label="Bob Token"
                  >
                    Bob Token
                  </a>
                </li>

                <li className="nft-link">
                  <a
                    href="https://opensea.io/collection/bobmonad/overview"
                    className="presale-btn dashboard-btn dashboard-subtab-btn dashboard-subtab-nfts"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Bob NFTs on OpenSea"
                  >
                    Bob NFTs
                  </a>
                </li>

                <li className="buy-link">
                  <a
                    href="/dashboard?tab=analytics"
                    className={`presale-btn buy-btn ${isDashboardRoute ? "is-active" : ""}`}
                    aria-label="Dashboard"
                    aria-current={isDashboardRoute ? "page" : undefined}
                  >
                    Dashboard
                  </a>
                </li>

                <li className="dashboard-link dashboard-subtab-link">
                  <a
                    href="https://gbob.fun"
                    className="presale-btn dashboard-btn dashboard-subtab-btn dashboard-subtab-analytics"
                    aria-label="Launchpad"
                  >
                    Launchpad
                  </a>
                </li>
              </>
            )}

            <li>
              <a
              href="https://x.com/bobmonad"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Twitter"
              >
                Twitter
              </a>
            </li>
            <li>
              <a
                href="https://t.me/+cHY1yu1-tVtiYWNh"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Telegram"
              >
                Telegram
              </a>
            </li>
            <li>
              <a
                href="https://discord.gg/c69kaPBcav"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Discord"
              >
                Discord
              </a>
            </li>
          </ul>
        </div>
      </nav>

      {showBobberPopup && (
        <div
          className="nav-bobber-popup-backdrop"
          onClick={closeBobberPopup}
        >
          <div
            className="nav-bobber-popup-shell"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="nav-bobber-popup-inner">
              {!bobberDone ? (
                <button
                  type="button"
                  className="nav-bobber-trigger"
                  onClick={handleBobberCheck}
                  aria-label="Check Bobber eligibility"
                  disabled={bobberLoading}
                  aria-busy={bobberLoading ? "true" : "false"}
                >
                  <div className="nav-bobber-stage">
                    <div
                      className={
                        "nav-bobber-hint " + (bobberLoading ? "is-hidden" : "")
                      }
                      aria-hidden="true"
                    >
                      <div className="nav-bobber-hint-inner">
                        <div className="nav-bobber-hint-title">
                          Click the bobber to check eligibility
                        </div>
                        <div className="nav-bobber-hint-sub">
                          {bobberMessage || "You may be asked to sign a message"}
                        </div>
                      </div>
                    </div>

                    {bobberLoading && (
                      <div className="nav-bobber-loading" aria-hidden="true">
                        Checking...
                      </div>
                    )}

                    <BobberEligibility
                      triggerKey={bobberTrigger}
                      isEligible={bobberEligible}
                      onDone={() => setBobberDone(true)}
                    />
                  </div>
                </button>
              ) : (
                <div
                  className={
                    "nav-bobber-result " +
                    (bobberEligible ? "eligible" : "ineligible")
                  }
                >
                  <div className="nav-bobber-hero-image">
                    <div className="nav-bobber-hero-inner">
                      <img
                        src={
                          bobberEligible
                            ? "/header/bobyes.jpg"
                            : "/header/bobno.jpg"
                        }
                        alt={
                          bobberEligible
                            ? "Eligible Bobber result"
                            : "Ineligible Bobber result"
                        }
                      />
                    </div>
                  </div>

                  <div className="nav-bobber-result-meta">
                    <div className="nav-bobber-result-pill">
                      {bobberEligible ? "Eligible" : "Ineligible"}
                    </div>

                    <p className="nav-bobber-result-title">
                      {bobberEligible
                        ? "This wallet is eligible."
                        : "This wallet is not eligible."}
                    </p>

                    <p className="nav-bobber-result-text">
                      {bobberEligible
                        ? "This wallet is eligible for the Bob NFT whitelist mint."
                        : "This wallet is not on the Bob NFT whitelist for this mint phase."}
                    </p>

                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default memo(Nav);
